import type { WaitlistEntry } from 'types/waitlist';

export type AutoBuyInDecision = {
  /** Whether the buy-in modal should be opened now. */
  open: boolean;
  /** The offer signature to remember, so the same offer window isn't re-opened. */
  sig: string | null;
};

/**
 * Decide whether to auto-open the buy-in modal for the table the player is
 * currently on, given their waitlist spot for that table.
 *
 * A seat is claimable when it's the player's turn, a seat is open, and they're
 * not already seated. To avoid re-popping the buy-in every broadcast (or after
 * the player dismisses it), each offer window is keyed on its accept-start time
 * + bumpCount; we only open when that signature changes. The signature resets
 * (→ null) when the player is no longer on that waitlist.
 *
 * Pure so the Game effect that drives it stays trivially testable.
 */
export function resolveAutoBuyInOffer(
  spot: WaitlistEntry | null | undefined,
  isSeated: boolean,
  lastSig: string | null
): AutoBuyInDecision {
  const ready = !!spot && spot.yourTurn && spot.availableSeats > 0 && !isSeated;
  if (!ready) {
    // Forget the last offer once the player leaves the waitlist, so a brand-new
    // future offer re-opens; keep it while still queued (not-yet-ready) so a
    // dismissed offer doesn't immediately re-pop.
    return { open: false, sig: spot ? lastSig : null };
  }
  const sig = `${spot!.waitForAcceptStartTime}|${spot!.bumpCount}`;
  if (lastSig === sig) return { open: false, sig };
  return { open: true, sig };
}
